import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { statusVariant } from '../../../utils/statusVariant';
import Badge, { BADGE_VARIANTS } from '../../../components/ui/Badge';
import { RFI_STATUSES, professionLabel, professionColor, statusLabel, formatDate, isAnswered } from './rfiConstants';

// Tableau des QRT, calque sur ExtrasTable : en-tetes triables, ligne cliquable
// (ouvre la fiche en consultation), pastille de statut, menu ⋮.

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig?.key !== column) {
    return <Icon name="ChevronsUpDown" size={12} className="text-gray-400" />;
  }
  return sortConfig?.direction === 'asc'
    ? <Icon name="ChevronUp" size={12} className="text-primary" />
    : <Icon name="ChevronDown" size={12} className="text-primary" />;
};

// ─── Pastille de statut interactive (ligne du tableau) ────────
// Calquee sur StatusPill d'ExtrasTable : Badge rendu en <button> + le menu
// partage PortalDropdown.
//
// 🔴 Aucune logique ici. Les trois lignes d'aiguillage sont celles du <select>
// qu'elle remplace : 'answered' ouvre la modale obligatoire, tout le reste
// passe par handleStatusChange. Le composant ne fait qu'appeler onSelect.
const RfiStatusPill = ({ rfi, onSelect, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  return (
    <div className="relative">
      <Badge
        as="button"
        ref={ref}
        variant={statusVariant('rfi', rfi?.status)}
        onClick={() => setOpen((v) => !v)}
        className="cursor-pointer select-none transition-opacity hover:opacity-80"
        title={t('bulk.changeStatus')}
      >
        {statusLabel(t, rfi?.status)}
        <Icon name="ChevronDown" size={10} />
      </Badge>
      <PortalDropdown
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        align="left"
        width={200}
        items={RFI_STATUSES?.map((s) => ({
          label: t(s?.labelKey) || s?.fallback,
          dot: BADGE_VARIANTS?.[statusVariant('rfi', s?.value)]?.dot,
          active: rfi?.status === s?.value,
          onClick: () => { if (rfi?.status !== s?.value) onSelect(s?.value); },
        }))}
      />
    </div>
  );
};

// Menu ⋮ — portail, jamais coupe par le defilement du tableau.
const ActionsMenu = ({ rfi, onView, onDownloadPDF, onDelete, t }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const answered = isAnswered(rfi?.status);

  const items = [
    { label: t('rfi.actions.view'), icon: 'Eye', onClick: () => onView?.(rfi) },
    { label: t('rfi.actions.downloadPdf'), icon: 'FileDown', onClick: () => onDownloadPDF?.(rfi) },
    {
      label: t('rfi.actions.delete'),
      icon: 'Trash2',
      onClick: () => onDelete?.(rfi?.id),
      variant: answered ? undefined : 'danger',
      disabled: answered,
      title: answered ? t('rfi.deleteLocked') : undefined,
    },
  ];

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        title={t('rfi.table.actions')}
      >
        <Icon name="MoreVertical" size={15} />
      </button>
      <PortalDropdown anchorRef={btnRef} open={open} onClose={() => setOpen(false)} items={items} width={200} />
    </>
  );
};

const RFITable = ({
  rfis,
  photoCounts,
  sortConfig,
  onSort,
  onStatusChange,
  onOpenResponse,
  onView,
  onDownloadPDF,
  onDelete,
  t,
  language,
}) => {
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const handleConfirmDelete = () => {
    if (pendingDeleteId != null) onDelete?.(pendingDeleteId);
    setPendingDeleteId(null);
  };

  if (!rfis?.length) {
    return (
      <div className="p-12 text-center">
        <Icon name="MessageSquare" size={40} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm text-gray-500">{t('rfi.empty.noRfis')}</p>
        <p className="text-xs text-gray-400 mt-1">{t('rfi.empty.createFirst')}</p>
      </div>
    );
  }

  const SortableHeader = ({ column, label, width }) => (
    <th
      className="text-left text-xs font-medium text-gray-500 px-3 py-3 cursor-pointer select-none bg-gray-50 hover:bg-gray-100"
      style={{ whiteSpace: 'nowrap', width: width || undefined }}
      onClick={() => onSort?.(column)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <SortIcon column={column} sortConfig={sortConfig} />
      </div>
    </th>
  );

  return (
    <>
      {pendingDeleteId != null && (
        <ConfirmDialog
          isOpen={pendingDeleteId != null}
          title={t('rfi.deleteDialog.title')}
          message={t('rfi.deleteDialog.message')}
          confirmVariant="danger"
          confirmLabel={t('rfi.deleteDialog.delete')}
          cancelLabel={t('rfi.deleteDialog.cancel')}
          onConfirm={handleConfirmDelete}
          onCancel={() => setPendingDeleteId(null)}
        />
      )}
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col style={{ width: '90px' }} />
          <col style={{ width: '140px' }} />
          <col />
          <col style={{ width: '90px' }} />
          <col style={{ width: '180px' }} />
          <col style={{ width: '120px' }} />
          <col style={{ width: '80px' }} />
        </colgroup>
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            <SortableHeader column="rfi_number" label={t('rfi.table.number')} width="90px" />
            <SortableHeader column="profession" label={t('rfi.table.profession')} width="140px" />
            <SortableHeader column="subject" label={t('rfi.table.subject')} />
            <SortableHeader column="photos" label={t('rfi.table.photos')} width="90px" />
            <SortableHeader column="status" label={t('rfi.table.status')} width="180px" />
            <SortableHeader column="created_at" label={t('rfi.table.date')} width="120px" />
            <th className="text-right text-xs font-medium text-gray-500 px-3 py-3 bg-gray-50" style={{ whiteSpace: 'nowrap', width: '80px' }}>
              {t('rfi.table.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rfis?.map((rfi) => (
            <tr
              key={rfi?.id}
              // Clic sur la ligne : ouvre la fiche (consultation). Les controles
              // de la ligne (pastille, menu) gardent leur role.
              onClick={(e) => {
                if (!onView) return;
                if (e?.target?.closest?.('button, a, input, select, textarea, label, [role="menu"], [role="menuitem"]')) return;
                onView(rfi);
              }}
              className="transition-colors cursor-pointer hover:bg-gray-50/60"
            >
              <td className="px-3 py-3 align-middle">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 text-orange-600 whitespace-nowrap">{rfi?.rfi_number}</span>
              </td>
              <td className="px-3 py-3 align-middle overflow-hidden" style={{ whiteSpace: 'nowrap' }}>
                {rfi?.profession ? (() => {
                  const c = professionColor(rfi?.profession);
                  return (
                    <span
                      className={`inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-[11px] font-medium leading-tight ${c?.bg} ${c?.text}`}
                      title={professionLabel(t, rfi?.profession)}
                    >
                      <span className="truncate">{professionLabel(t, rfi?.profession)}</span>
                    </span>
                  );
                })() : <span className="text-xs text-gray-400">—</span>}
              </td>
              {/* Sujet + apercu de la question dessous (meme rendu qu'ExtrasTable) */}
              <td className="px-3 py-3 align-middle min-w-0">
                <p className="text-xs text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis leading-tight" title={rfi?.subject || undefined}>{rfi?.subject}</p>
                {rfi?.question?.trim() && (
                  <p className="text-[10px] text-gray-400 leading-tight truncate mt-0.5" title={rfi?.question}>{rfi?.question}</p>
                )}
              </td>
              {/* Nombre de photos de la question */}
              <td className="px-3 py-3 align-middle text-xs whitespace-nowrap leading-tight">
                {photoCounts?.[rfi?.id] > 0 ? (
                  <span className="inline-flex items-center gap-1 text-gray-700">
                    <Icon name="Image" size={12} className="text-gray-400" />
                    {photoCounts?.[rfi?.id]}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              {/* Le clic sur la pastille ne doit pas ouvrir la fiche. */}
              <td className="px-3 py-3 align-middle" onClick={(e) => e?.stopPropagation()}>
                <RfiStatusPill
                  rfi={rfi}
                  t={t}
                  onSelect={async (newStatus) => {
                    // 'answered' passe OBLIGATOIREMENT par la modale de
                    // reponse, jamais par handleStatusChange.
                    if (newStatus === 'answered') await onOpenResponse?.(rfi);
                    else await onStatusChange?.(rfi?.id, newStatus);
                  }}
                />
              </td>
              <td className="px-3 py-3 align-middle text-gray-500 text-xs whitespace-nowrap leading-tight">{formatDate(rfi?.created_at, language)}</td>
              <td className="px-3 py-3 align-middle text-right" onClick={(e) => e?.stopPropagation()}>
                <ActionsMenu
                  rfi={rfi}
                  t={t}
                  onView={onView}
                  onDownloadPDF={onDownloadPDF}
                  onDelete={(id) => setPendingDeleteId(id)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default RFITable;
